/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - European Union 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [editable-box.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 11.10.18 17:44
 */

// Emoji documentation:
// https://github.com/emojione/emojione/blob/master/INSTALLATION.md
// https://www.webpagefx.com/tools/emoji-cheat-sheet/
// https://www.emojicopy.com/
// https://stackoverflow.com/questions/9284117/inserting-arbitrary-html-into-a-documentfragment
// http://help.dottoro.com/ljnjoumd.php
// https://html5-editor.net/
// https://ourcodeworld.com/articles/read/282/how-to-get-the-current-cursor-position-and-selection-within-a-text-input-or-textarea-in-javascript
// https://hackernoon.com/easily-create-an-html-editor-with-designmode-and-contenteditable-7ed1c465d39b
// http://jsfiddle.net/jwvha/1/
// ***! https://stackoverflow.com/questions/6690752/insert-html-at-caret-in-a-contenteditable-div

import emojione from '../../../node_modules/emojione/lib/js/emojione';
import React, {useEffect, useRef, useState} from 'react';
import EmojiToggler from './emoji-toggler';
import {RoundedButton, Tooltip} from "../navigation/buttons/nav-buttons";
import {useIsMobile, useLanguage} from "../configuration/configuration";
import {NavigationGroup, NavigationRow} from "../navigation/layout/nav-layout";
import {
    AddPhotoIcon, fs_bigger,
    SendIcon,
    SoundcloudBrandIcon,
    VideoUploadIcon,
    YoutubeSquareIcon
} from "../navigation/icons/nav-icons";
import DOMPurify from "dompurify";
import {Popover} from "../navigation/popover/popover";
import PostVisibilityPopover from "../popover/post-visibility-popover";
import {getGenericVisibilityFromLocalStorage, getHomeVisibilityFromLocalStorage} from "../../actions/local-storage";
import SpaceAccessIcon from "../navigation/icons/space-access-icon";
import GenericPostVisibilityPopover from "../popover/generic-post-visibility-popover";
import {useIsHomeSpaceLocation} from "../../selectors";

// emojione.imageType = 'png';
// emojione.sprites = true;
emojione.ascii = true;
// emojione.imagePathPNG = 'https://cdn.jsdelivr.net/emojione/assets/' + ns.emojiVersion + '/png/';
emojione.imagePathPNG = '/static/emojione-assets/png/32/';

const pasteHtmlAtCaret = (html) => {
    let selection, range;
    let node = document.createElement("span");
    node.innerHTML = html;
    if (window.getSelection) {   // IE9 and non-IE
        selection = window.getSelection();
        range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(node);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range)
    } else if (document.selection && document.selection.type !== "Control") {
        document.selection.createRange().pasteHTML(html);    // IE < 9
    }
}

const EmojiEditor = ({id, text, mediaupload, youtube, videoUpload, soundcloud, placeholder, callback,
                         navigation = true}) => {
    const [visibilityForm, setVisibilityForm] = useState(null);
    const editableBoxRef = useRef(null);
    const isMobile = useIsMobile();
    const Lang = useLanguage();
    const {isHomeSpace} = useIsHomeSpaceLocation();

    useEffect(() => {
        if (editableBoxRef && text) {
            const sanitized = DOMPurify.sanitize(text, {USE_PROFILES: {html: true}});
            editableBoxRef.current.innerHTML = emojione.toImage(sanitized);
        }
    }, [text])


    const handleEditorEnter = (event) => {
        event.preventDefault();
        let sanitized = DOMPurify.sanitize(editableBoxRef.current.innerHTML, {ALLOWED_TAGS: ['img', 'br']});
        const emoji = emojione.toShort(sanitized);
        callback(emoji);
        editableBoxRef.current.innerHTML = "";
    }

    const handleEmojiShortName = (shortName) => {
        editableBoxRef.current.focus();
        shortName && pasteHtmlAtCaret(emojione.toImage(shortName));
    }

    const renderDesktopEditor = id => {
        return <div className="d-flex flex-column align-items-end">
            <div contentEditable="true" className="editable-box-content pre-wrap"
                 placeholder={placeholder ? placeholder : Lang.post.editor.placeholder}
                 autoFocus
                 ref={editableBoxRef}/>
            <EmojiToggler id={id} onClick={handleEmojiShortName} onEnter={handleEditorEnter}/>
        </div>
    }

    const renderMobileEditor = id => {
        return <div className="d-flex flex-column align-items-end">
                <div contentEditable="true" className="editable-box-content w-100"
                     placeholder={placeholder ? placeholder : Lang.post.editor.placeholder}
                     autoFocus
                     ref={editableBoxRef}/>
                <div className="mt-1 mr-2">
                    <RoundedButton className='btn-primary' title={Lang.post.editor.saveTooltip}
                                   onClick={handleEditorEnter}>
                        <SendIcon big aria-hidden="true" className='clr-white'/>
                    </RoundedButton>
                </div>
        </div>
    }

    // visibility and access rights for post by home or generic space
    const handleVisibility = ({close}) => {
        const popover = isHomeSpace ?
            <PostVisibilityPopover postId={null} onUpdate={handleOnUpdateVisibility} onClose={close}/> :
            <GenericPostVisibilityPopover postId={null} onUpdate={handleOnUpdateVisibility} onClose={close}/>;

        return <div>{popover}</div>
    }

    // required to update visibility icon
    const handleOnUpdateVisibility = formData => {
        setVisibilityForm(formData);
    };

    const getAccessFromLocalStorage = () => {
        const {access} = isHomeSpace ?
            getHomeVisibilityFromLocalStorage() :
            getGenericVisibilityFromLocalStorage();
        return access;
    }

    return (
        <div className='emoji-editor'>
            {navigation && <NavigationRow>
                <NavigationGroup>
                    <Tooltip>
                        <Popover className='popover-status-container'
                                 placement='bottom'
                                 render={handleVisibility}>
                            <RoundedButton className="btn-outline-secondary icon-padding-adj">
                                    <SpaceAccessIcon className="role-hover-white-btn" access={getAccessFromLocalStorage()}/>
                            </RoundedButton>
                        </Popover>
                    </Tooltip>
                </NavigationGroup>
                <NavigationGroup>
                    <RoundedButton className='btn-outline-secondary p-1 mr-1' title={Lang.post.editor.uploadImageTooltip}
                                   onClick={mediaupload}>
                        <AddPhotoIcon size={fs_bigger} aria-hidden="true"/>
                    </RoundedButton>

                    {videoUpload && <RoundedButton className='btn-outline-secondary p-1 mr-1' title={Lang.post.editor.uploadVideoTooltip} onClick={videoUpload}>
                        <VideoUploadIcon size={fs_bigger} aria-hidden="true"/>
                    </RoundedButton>}

                    <RoundedButton className='btn-outline-secondary p-1 mr-1' title={Lang.post.editor.youTubeTooltip} onClick={youtube}>
                        <YoutubeSquareIcon size={fs_bigger} aria-hidden="true"/>
                    </RoundedButton>

                    <RoundedButton className='btn-outline-secondary p-1 mr-2' title={Lang.post.editor.soundcloudTooltip} onClick={soundcloud}>
                        <SoundcloudBrandIcon size={fs_bigger} aria-hidden="true"/>
                    </RoundedButton>
                </NavigationGroup>
            </NavigationRow>}

            {isMobile ? renderMobileEditor(id) : renderDesktopEditor(id)}
            {/*{renderTextAreaEditor(id)}*/}
        </div>
    )
}

export default EmojiEditor;
